import { z } from 'zod';

import { REPORT_PERIOD, REPORT_PERIODS } from '@/features/reports/periods';
import { type ReportsSearch } from '@/features/reports/ReportsPage';
import { XERO_SYNC_CHECKS, XERO_SYNC_STATE } from '@/features/reports/xero/api';
import {
    CLIENTS_VIEW,
    DEFAULT_APP_USAGE_DAYS,
    DEFAULT_HEATMAP_WEEKS,
    isAppUsageDays,
    isHeatmapWeeks,
    QUEUE_BUCKETS,
    QUEUE_ORDER_STATUSES,
    QUEUE_PAYMENT_STATUSES,
    REPORT_TAB,
    REPORT_TABS,
    REPORT_VIEWS,
    TRANSACTIONS_VIEW,
} from '@/features/reports/tabs';

/*
  Reports (ALS-85, ALS-86, ALS-87, ALS-88, ALS-89, ALS-91, ALS-160, ALS-388). The tab, the period,
  the view, the relationship manager, the inactive window, the order queue's filters, the heatmap's
  window and lookbook, Brand intelligence's brand and quarter, App usage's window and Xero sync's
  check and list ride the URL, so a report can be shared and
  survives a reload. A value that names nothing lands on the default rather than throwing the
  route, and a default is left out of the address altogether. The relationship manager is the
  page's, as the prototype's header filter is: the queue's RM and Clients' are one choice.
*/
const day = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined);
const id = z.coerce.number().int().positive().optional().catch(undefined);
const text = z.string().optional().catch(undefined);

export const reportsSearch = z.object({
    tab: z.enum(REPORT_TABS).optional().catch(undefined),
    period: z.enum(REPORT_PERIODS).optional().catch(undefined),
    view: z.enum(REPORT_VIEWS).optional().catch(undefined),
    // Clients' inactive window in days; the API refuses one outside 1 to 1,825, so neither lands here.
    inactive: z.coerce.number().int().min(1).max(1825).optional().catch(undefined),
    // App usage's window: 7, 30 or 90 days, as the API accepts; anything else is the default.
    days: z.coerce.number().refine(isAppUsageDays).optional().catch(undefined),
    // The Client heatmap's window: 6, 4 or 2 weeks, as the API accepts; anything else is the default.
    weeks: z.coerce.number().refine(isHeatmapWeeks).optional().catch(undefined),
    // Lookbook page attention's lookbook (ALS-89); absent is the most viewed in the window.
    lookbook: id,
    q: text,
    bucket: z.enum(QUEUE_BUCKETS).optional().catch(undefined),
    // Only what the API accepts, so an old or hand-edited link lands on the unfiltered queue
    // rather than on a view that cannot load.
    status: z.enum(QUEUE_ORDER_STATUSES).optional().catch(undefined),
    payment: z.enum(QUEUE_PAYMENT_STATUSES).optional().catch(undefined),
    rmId: id,
    brandId: id,
    categoryId: id,
    supplierId: id,
    from: day,
    to: day,
    // Brand intelligence's brand, apart from the queue's brandId filter so neither leaks into the other.
    brand: id,
    quarter: z
        .string()
        .regex(/^\d{4}-Q[1-4]$/)
        .optional()
        .catch(undefined),
    // Xero sync (ALS-388): one check's differences, and the dismissed ones instead of the open.
    check: z.enum(XERO_SYNC_CHECKS).optional().catch(undefined),
    findings: z.literal(XERO_SYNC_STATE.Dismissed).optional().catch(undefined),
});

export type RouteSearch = z.infer<typeof reportsSearch>;

/** The search as the address carries it: defaults and empty values left out. */
export function toAddress({
    tab,
    period,
    view,
    rmId,
    inactiveDays,
    brand,
    quarter,
    days,
    weeks,
    lookbookId,
    queue,
    xero,
}: ReportsSearch): RouteSearch {
    const address: RouteSearch = {
        tab: tab === REPORT_TAB.Overview ? undefined : tab,
        period: period === REPORT_PERIOD.Month ? undefined : period,
        // Each tab's first view is its default.
        view: view === TRANSACTIONS_VIEW.Orders || view === CLIENTS_VIEW.Spend ? undefined : view,
        rmId,
        inactive: inactiveDays,
        brand,
        quarter,
        days: days === DEFAULT_APP_USAGE_DAYS ? undefined : days,
        weeks: weeks === DEFAULT_HEATMAP_WEEKS ? undefined : weeks,
        lookbook: lookbookId,
        ...queue,
        check: xero?.check,
        findings: xero?.state === XERO_SYNC_STATE.Dismissed ? XERO_SYNC_STATE.Dismissed : undefined,
    };
    return Object.fromEntries(
        Object.entries(address).filter(([, value]) => value !== undefined && value !== ''),
    );
}
